import type { BigDecimal } from "@solomon-labs/bigdecimal";
import React from "react";

import { HeroSection } from "@/pages/USDv/components/HeroSection";
import { USDvLandingPanel } from "@/pages/USDv/components/USDvLandingPanel";

import type { DistributionMode } from "../application/types";
import { heroModeForVariant } from "../helpers/landingVariant";
import type { ResolvedView } from "../state/types";

import { PortfolioSummary } from "./PortfolioSummary";

/**
 * Left-column hero — picks between `PortfolioSummary` (active position)
 * and `HeroSection` (no position) and renders both the desktop slot
 * (inside `USDvLandingPanel`) and the mobile stacked surface in one
 * place so the page doesn't duplicate the branching twice.
 *
 * The desktop slot shares with `/` (USDvPage) so all landing-style
 * surfaces render the literal-same hero chrome.
 */
export function LeftHero({
    resolved,
    isEnrolled,
    averageAPY,
    distributionMode,
    isBelowMinimum,
    isDisabled,
    walletBalance
}: {
    resolved: ResolvedView;
    /** The wallet is enrolled. Drives WHICH hero renders. */
    isEnrolled: boolean;
    averageAPY: string | null;
    distributionMode: DistributionMode;
    /** Mirrors PositionsSection's derivation. Passed in rather than
     *  recomputed so the two columns cannot disagree about whether the
     *  wallet is earning. */
    isBelowMinimum: boolean;
    /** Position status is Disabled. Stops accrual with below-minimum. */
    isDisabled: boolean;
    /** On-chain USDv balance, for the accrual base before a Position. */
    walletBalance: BigDecimal | number;
}): React.ReactElement {
    const heroContent = isEnrolled ? (
        <PortfolioSummary
            walletStats={resolved.walletStats}
            averageAPY={averageAPY}
            address={resolved.address}
            earliestPositionCreatedAt={resolved.earliestPositionCreatedAt}
            distributionMode={distributionMode}
            isBelowMinimum={isBelowMinimum}
            isDisabled={isDisabled}
            walletBalance={walletBalance}
            minBalance={resolved.minBalance}
            nextDistributionDate={resolved.nextDistributionDate}
            projectedNextDistributionDate={
                resolved.projectedNextDistributionDate
            }
            distributionPeriodStartsAt={resolved.distributionPeriodStartsAt}
        />
    ) : resolved.isWalletConnected ? (
        <HeroSection
            mode={heroModeForVariant(resolved.landingVariant)}
            stablesBalance={resolved.stablesBalance}
        />
    ) : undefined;

    return (
        <>
            <USDvLandingPanel
                hero={
                    isEnrolled ? (
                        <div className="w-full">{heroContent}</div>
                    ) : (
                        heroContent
                    )
                }
            />

            {/* Mobile hero — stacked above the action card. Padding
                removed so the guilloche backdrop bleeds edge-to-edge. */}
            <div className="flex md:hidden flex-col items-center justify-center">
                {isEnrolled ? (
                    <div className="w-full">{heroContent}</div>
                ) : (
                    (heroContent ?? (
                        <HeroSection
                            mode="default"
                            stablesBalance={resolved.stablesBalance}
                        />
                    ))
                )}
            </div>
        </>
    );
}
